import React, { ChangeEvent } from "react";
import {
    Box,
    BoxProps,
    Button,
    Card,
    CardProps,
    Checkbox,
    Chip,
    Collapse,
    FormControl,
    FormControlLabel,
    FormControlProps,
    InputLabel,
    LinearProgress,
    ListItemButton,
    ListItemButtonProps,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableCellProps,
    TableContainer,
    TableContainerProps,
    TableFooter,
    TableFooterProps,
    TableHead,
    TablePagination,
    TablePaginationProps,
    TableRow,
    TableSortLabel,
    TextField,
    TextFieldProps,
    Typography,
    TypographyProps,
    useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { Check, ExpandLess, ExpandMore } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { statusStyleCertificate } from "utilities/colorHelper";
import * as Styles from "pages/employee/certificates/listRenews/listRenews.styles";
import * as certificateService from "services/certificate-service";
import * as locationService from "services/location-service";
import * as departmentService from "services/department-service";
import * as authenticationService from "services/AuthenticationService";
import {
    getLocalStorage,
    getSessionStorage,
    parseSessionStorage,
    pasrseLocalStorage,
} from "utilities/sessionStorage";
import RenewBulkModal from "./modals/renewBulkModal";
import DataTable from "components/formsUI/DataTable";
import {
    GridColumnVisibilityModel,
    GridInputRowSelectionModel,
    GridPaginationModel,
    MuiEvent,
} from "@mui/x-data-grid";
import { useAuthContext } from "contexts/authContext";
import "dayjs/locale/nl";
import "dayjs/locale/fr";
import "dayjs/locale/en";
import DatePicker from "components/formsUI/DatePicker/DatePicker";

interface IProps {}

interface filter {
    dateFrom: Date | null;
    dateTo: Date | null;
    dateCreated: Date | null;
    department: string;
    organisation: string;
    status: string[];
    location: string[];
    referenceCode: string;
    requester: string;
    order: "asc" | "desc";
    orderBy: string;
    currentPage: number;
    rowsPerPage: number;
    renewCreationDate: Date | null;
    renewReferenceCode: string;
}

const initialColumnVisibility: { [key: string]: boolean } = {
    department: false,
    organization: false,
};

const ListRenews = (props: IProps) => {
    const { user, checkAuth } = useAuthContext();
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const dateToday = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate(),
    );
    const [sessionStorageComplete, setSessionStorageComplete] =
        React.useState<boolean>(false);

    // Error
    const [showErrorMessage, setShowErrorMessage] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");

    const showError = (message: string) => {
        setErrorMessage(message);
        setShowErrorMessage(true);
    };

    // Table
    const [headers, setHeaders] = React.useState<{ [key: string]: boolean }>(
        initialColumnVisibility,
    );
    const [certificates, setCertificates] = React.useState<
        CertificatePagedDTO[]
    >([]);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [count, setCount] = React.useState(0);
    const [locations, setLocations] = React.useState<LocationList[]>([]);

    const [openBulkRenew, setOpenBulkRenew] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const [filtersChanged, setFiltersChanged] = React.useState(false);
    const [filtersChangedDate, setFiltersChangedDate] = React.useState("");

    const [order, setOrder] = React.useState<"asc" | "desc">("desc");
    const [orderBy, setOrderBy] = React.useState("creationTime");
    const [dateFrom, setDateFrom] = React.useState<Date | undefined | null>(
        null,
    );
    const [dateTo, setDateTo] = React.useState<Date | undefined | null>(null);
    const [dateCreated, setDateCreated] = React.useState<
        Date | undefined | null
    >(null);
    const [referenceCode, setReferenceCode] = React.useState("");
    const [requester, setRequester] = React.useState("");
    const [initial, setInitial] = React.useState(true);
    const [selected, setSelected] = React.useState<
        { id: string; checked: boolean; reference: string }[]
    >([]);

    // --- Multiselect setup ---
    const [status, setStatus] = React.useState<string[]>([]);
    const [location, setLocation] = React.useState<string[]>([]);
    const [departments, setDepartments] = React.useState<string[]>([]);
    const [selectedDepartments, setSelectedDepartments] = React.useState<
        string[]
    >([]);
    const [organisation, setOrganisation] = React.useState<string>("");
    const [renewReference, setRenewReference] = React.useState<string>("");
    const [renewCreationDate, setRenewCreationDate] = React.useState<
        Date | undefined | null
    >(null);
    const [timeouts, setTimeouts] = React.useState<NodeJS.Timeout[]>([]);
    const handleChange = (
        event: SelectChangeEvent<string[]>,
        field: "locations" | "status",
    ) => {
        const {
            target: { value },
        } = event;
        // On autofill we get a stringified value.
        if (field === "locations") {
            const locations =
                typeof value === "string" ? value.split(",") : value;
            setLocation(locations);
            parse({ location: locations });
        } else if (field === "status") {
            const statuses =
                typeof value === "string" ? value.split(",") : value;
            setStatus(statuses);
            parse({ status: statuses });
        }

        setFiltersChanged(true);
    };

    const debounce = () => {
        timeouts.forEach((timeout) => {
            clearTimeout(timeout);
            clearInterval(timeout);
        });
        setTimeouts([]);
        const _timeout = setTimeout(() => {
            getAllInfo();
        }, 500);
        setTimeouts([...timeouts, _timeout]);
    };

    const handleReset = () => {
        setOrder("desc");
        setOrderBy("creationTime");
    };

    const handleOrgChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setOrganisation(event.target.value);
        parse({ organisation: event.target.value });
        setFiltersChanged(true);
    };

    const handleDepartmentChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;
        const departments =
            typeof value === "string" ? value.split(",") : value;
        setSelectedDepartments(departments);
        parse({ department: departments });
        setFiltersChanged(true);
    };

    const handleHeaderChange = (model: GridColumnVisibilityModel) => {
        for (const key of Object.keys(model)) {
            const value = model[key] as boolean;
            if (value) continue;
            switch (key) {
                case "referenceCode":
                    setReferenceCode("");
                    break;
                case "creationTime":
                    setDateCreated(null);
                    break;
                case "requester":
                    setRequester("");
                    break;
                case "dateFrom":
                    setDateFrom(null);
                    break;
                case "dateTo":
                    setDateTo(null);
                    break;
                case "renewDate":
                    setRenewCreationDate(null);
                    break;
                case "renewReference":
                    setRenewReference("");
                    break;
                case "department":
                    setSelectedDepartments([]);
                    break;
                case "organisation":
                    setOrganisation("");
                    break;
                case "locations":
                    setLocation([]);
                    break;
                case "status":
                    setStatus(["Geldig", "Verlopen"]);
                    break;
            }
        }
        pasrseLocalStorage<GridColumnVisibilityModel>("renews", model);
        setHeaders(model);
    };

    const handleRenewOpen = (): void => {
        if (selected.filter((item) => item.checked).length > 1) {
            setOpenBulkRenew(true);
        } else {
            navigate(`${selected[0].id}`);
        }
    };

    const updateCheckItem = (id: string, reference: string) => {
        const temp = selected.filter((item) => item.id !== id);
        setSelected([
            ...temp,
            {
                id: id,
                checked: !selected.find((item) => item.id === id)?.checked,
                reference: reference,
            },
        ]);
    };

    const statuses = checkAuth(["BeheerAttesten", "IntrekkenAttesten"], false)
        ? [
              "Goed te keuren",
              "Geldig",
              "Uit dienst",
              "Toekomstig geldig",
              "Verlopen",
              "Ingetrokken",
          ]
        : ["Goed te keuren", "Geldig", "Toekomstig geldig", "Verlopen"];

    const fetchCertificates = (
        currentPage: number,
        rowsPerPage: number,
        order: string,
        orderBy: string,
    ) => {
        setErrorMessage("");
        setShowErrorMessage(false);
        setCertificates([]);
        setIsLoading(true);
        var statusJoin = status.join(",");
        var locationsJoin = location.join(",");
        var departments = selectedDepartments.join(",");
        certificateService
            .getCertificates(
                currentPage + 1,
                rowsPerPage,
                order,
                orderBy,
                referenceCode,
                dateFrom,
                dateTo,
                dateCreated,
                requester,
                locationsJoin,
                statusJoin,
                departments,
                organisation,
                null,
                renewCreationDate,
                renewReference,
                "renew-paged",
            )
            .then((res: CertificatesPagedResponse) => {
                setIsLoading(false);
                setFiltersChanged(false);
                setCount(res.rowCount);
                setCertificates(res.results);
                setFiltersChangedDate("");
            })
            .catch((error: AxiosError<Error>) => {
                setIsLoading(false);
                showError(`${t("certificates.error")}`);
            });
    };

    const fetchLocations = () => {
        setErrorMessage("");
        setShowErrorMessage(false);
        locationService
            .getLocations()
            .then((res: LocationList[]) => {
                setLocations(res);
            })
            .catch((error: AxiosError<Error>) => {
                showError(`${t("certificates.error")}`);
            });
    };

    const checkKeyEnter = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            debounce();
            // fetchCertificates(currentPage, rowsPerPage, order, orderBy);
        }
    };

    React.useEffect(() => {
        if (!sessionStorageComplete && initial) {
            const data = getSessionStorage<filter>("renews");
            if (data) {
                if (data.dateFrom) setDateFrom(data.dateFrom);
                if (data.dateTo) setDateTo(data.dateTo);
                if (data.dateCreated) setDateCreated(data.dateCreated);
                if (data.department)
                    setSelectedDepartments(
                        typeof data.department === "string"
                            ? data.department.split(",")
                            : data.department,
                    );
                if (data.organisation) setOrganisation(data.organisation);
                if (data.referenceCode) setReferenceCode(data.referenceCode);
                if (data.requester) setRequester(data.requester);
                if (data.order) setOrder(data.order);
                if (data.orderBy) setOrderBy(data.orderBy);
                if (data.currentPage) setCurrentPage(data.currentPage);
                if (data.rowsPerPage) setRowsPerPage(data.rowsPerPage);
                if (data.location) setLocation(data.location);
                if (data.status) {
                    const temp = ["Geldig", "Verlopen", ...data.status];
                    temp.filter((el, index) => temp.indexOf(el) === index);
                    setStatus(temp);
                }
                setSessionStorageComplete(true);
            } else {
                setSessionStorageComplete(true);
            }
            const headers =
                getLocalStorage<GridColumnVisibilityModel>("renews");
            if (headers) setHeaders(headers);
        }

        if (status.length <= 0 && initial) {
            console.log("reset status");
            setStatus(["Geldig", "Verlopen"]);
        }

        if (sessionStorageComplete) {
            debounce();
            // getAllInfo();
            if (departments.length <= 0) {
                departmentService.getDepartmentNames().then((res: string[]) => {
                    setDepartments([...res]);
                });
            }
        }
    }, [
        dateFrom,
        dateTo,
        dateCreated,
        selectedDepartments,
        organisation,
        status,
        referenceCode,
        requester,
        order,
        orderBy,
        currentPage,
        rowsPerPage,
        location,
        sessionStorageComplete,
        renewCreationDate,
        renewReference,
    ]);

    const getAllInfo = () => {
        if (initial) {
            fetchLocations();
            setInitial(false);
        }

        if (filtersChangedDate === "") {
            fetchCertificates(currentPage, rowsPerPage, order, orderBy);
        } else {
            var temp = null;
            if (filtersChangedDate === "dateCreated" && dateCreated)
                temp = new Date(dateCreated).getTime();
            else if (filtersChangedDate === "dateFrom" && dateFrom)
                temp = new Date(dateFrom).getTime();
            else if (filtersChangedDate === "dateTo" && dateTo)
                temp = new Date(dateTo).getTime();
            else if (filtersChangedDate === "renewed" && renewCreationDate)
                temp = new Date(renewCreationDate).getTime();
            if (
                (temp && !isNaN(temp)) ||
                (filtersChangedDate === "dateCreated" &&
                    dateCreated === null) ||
                (filtersChangedDate === "dateFrom" && dateFrom === null) ||
                (filtersChangedDate === "dateTo" && dateTo === null) ||
                (filtersChangedDate === "renewed" && renewCreationDate === null)
            ) {
                setFiltersChangedDate("");
                fetchCertificates(currentPage, rowsPerPage, order, orderBy);
            }
        }
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.id === "requester") {
            setRequester(event.target.value);
            parse({ requester: event.target.value });
        } else if (event.target.id === "referenceCode") {
            setReferenceCode(event.target.value);
            parse({ referenceCode: event.target.value });
        } else if (event.target.id === "renewReferenceCode") {
            setRenewReference(event.target.value);
            parse({ renewReferenceCode: event.target.value });
        }
        setCurrentPage(0);
        setFiltersChanged(true);
    };

    const handleDateFilterChange = (
        date: Date | null | undefined,
        keyboardInputValue: string | undefined,
        field: string = "",
    ) => {
        if (field === "dateCreated") {
            setFiltersChangedDate("dateCreated");
            setDateCreated(date);
            parse({ dateCreated: date });
        } else if (field === "dateFrom") {
            setFiltersChangedDate("dateFrom");
            setDateFrom(date);
            parse({ dateFrom: date });
        } else if (field === "dateTo") {
            setFiltersChangedDate("dateTo");
            setDateTo(date);
            parse({ dateTo: date });
        } else if (field === "renew") {
            setFiltersChangedDate("renewed");
            setRenewCreationDate(date);
            parse({ renewCreationDate: date });
        }
        setCurrentPage(0);
        setFiltersChanged(true);
    };

    const handleCertificateSort = (property: string) => {
        if (orderBy === property) {
            if (order === "asc") {
                setOrder("desc");
                parse({ order: "desc" });
            } else {
                setOrder("asc");
                parse({ order: "asc" });
            }
            setCurrentPage(0);
        } else {
            setOrderBy(property);
            setOrder("desc");
            parse({ order: "desc", orderBy: property });
            setCurrentPage(0);
        }
    };

    const handleClickCertificate = (certificate: CertificatePagedDTO) => {
        navigate(`/beheer/attesten/${certificate.id}`);
    };

    const handleCellClick = (
        row: any,
        event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
    ) => {
        if (row.field === "renewReference") {
            handleClickRenew(row.row.renew, event);
        }
    };

    const handleClickRenew = (
        application: ApplicationWithDetails,
        event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
    ) => {
        if (application === null) return;
        if (
            application.referenceCode !== null &&
            application.referenceCode !== undefined &&
            application.referenceCode !== ""
        ) {
            event.stopPropagation();
            navigate(`/beheer/aanvragen/${application.id}`);
        }
    };

    const updatePagination = (model: GridPaginationModel) => {
        const { page, pageSize } = model;
        setCurrentPage(page);
        setRowsPerPage(pageSize);
        parse({ currentPage: page, rowsPerPage: pageSize });
    };

    const checkFilterUpdateData = () => {};

    const parse = (filter: { [key: string]: any }): void => {
        const obj: filter = {
            dateFrom: "dateFrom" in filter ? filter.dateFrom : dateFrom,
            department:
                "department" in filter
                    ? filter.department
                    : selectedDepartments,
            organisation:
                "organisation" in filter ? filter.organisation : organisation,
            status: "status" in filter ? filter.status : status,
            referenceCode:
                "referenceCode" in filter
                    ? filter.referenceCode
                    : referenceCode,
            requester: "requester" in filter ? filter.requester : requester,
            order: "order" in filter ? filter.order : order,
            orderBy: "orderBy" in filter ? filter.orderBy : orderBy,
            currentPage:
                "currentPage" in filter ? filter.currentPage : currentPage,
            rowsPerPage:
                "rowsPerPage" in filter ? filter.rowsPerPage : rowsPerPage,
            dateTo: "dateTo" in filter ? filter.dateTo : dateTo,
            dateCreated:
                "dateCreated" in filter ? filter.dateCreated : dateCreated,
            location: "location" in filter ? filter.location : location,
            renewCreationDate:
                "renewCreationDate" in filter
                    ? filter.renewCreationDate
                    : renewCreationDate,
            renewReferenceCode:
                "renewReferenceCode" in filter
                    ? filter.renewReferenceCode
                    : renewReference,
        };
        parseSessionStorage<filter>("renews", obj);
    };

    const handleSelectionChange = (selectionModel: any[]) => {
        const certs = certificates.filter((cert) =>
            selectionModel.includes(cert.id),
        );
        setSelected(
            certs.map((cert) => {
                return {
                    id: cert.id,
                    checked: true,
                    reference: cert.referenceCode,
                };
            }),
        );
    };

    // --- Props ---
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const tableFilterProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const listCertificatesWrapperProps: CardProps = {
        sx: Styles.listCertificatesWrapper(theme),
    };

    const listCertificatesTitleProps: TypographyProps = {
        variant: "h4",
        sx: Styles.listCertificatesTitle(theme),
    };

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme),
    };

    const tableContainerProps: TableContainerProps = {
        sx: Styles.tableContainer(theme),
    };

    const tableSearchProps: BoxProps = {
        sx: Styles.tableSearch(theme),
    };

    const tableTextFieldProps: TextFieldProps = {
        sx: Styles.tableTextField(theme),
    };

    const statusFilterProps: FormControlProps = {
        size: "small",
        sx: Styles.statusFilter(theme),
    };

    const departmentFilterProps: FormControlProps = {
        size: "small",
        sx: Styles.departmentFilter(theme),
    };

    const getStatus = (certificate: CertificatePagedDTO) => {
        const outOfServiceDate = dayjs(certificate.outOfServiceDate).startOf(
            "day",
        );

        if (certificate.isRevoked) return "Ingetrokken";
        else if (
            !certificate.isRevoked &&
            new Date(certificate.dateTo) < dateToday
        )
            return "Verlopen";
        else if (
            !certificate.isRevoked &&
            (outOfServiceDate.isSame(dayjs(dateToday).startOf("day")) ||
                outOfServiceDate.isBefore(dayjs(dateToday).startOf("day")))
        )
            return "Uit dienst";
        else if (
            certificate.isApproved &&
            !certificate.isRevoked &&
            new Date(certificate.dateFrom) > dateToday
        )
            return "Toekomstig geldig";
        else if (
            certificate.isApproved &&
            !certificate.isRevoked &&
            new Date(certificate.dateFrom) <= dateToday &&
            new Date(certificate.dateTo) >= dateToday
        )
            return "Geldig";
        else if (!certificate.isApproved && !certificate.isRevoked)
            return "Goed te keuren";

        return "";
    };

    return (
        <Card {...listCertificatesWrapperProps}>
            <Box {...titleWrapperProps}>
                <Typography {...listCertificatesTitleProps}>
                    {t("renew.title")}
                </Typography>
                {showErrorMessage && (
                    <Box color="error.main">
                        <Box>{errorMessage}</Box>
                        <Button
                            sx={{ marginTop: "5px" }}
                            color="error"
                            variant="outlined"
                            onClick={() => {
                                getAllInfo();
                            }}
                        >
                            {t("certificates.errorButton")}
                        </Button>
                    </Box>
                )}
            </Box>

            <Box {...tableContainerProps}>
                <DataTable
                    rows={certificates}
                    columns={[
                        {
                            field: "referenceCode",
                            headerName: t(
                                "certificatesTableHeader.referenceCode",
                            ),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        id="referenceCode"
                                        label={t(
                                            "certificatesTableHeader.referenceCode",
                                        )}
                                        size="small"
                                        onChange={handleFilterChange}
                                        value={referenceCode}
                                        onBlur={() => checkFilterUpdateData()}
                                        onKeyDown={checkKeyEnter}
                                    />
                                    <TableSortLabel
                                        active={orderBy === "referenceCode"}
                                        direction={
                                            orderBy === "referenceCode"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "referenceCode",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                        },
                        {
                            field: "requester",
                            headerName: t("certificatesTableHeader.requester"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        id="requester"
                                        label={t(
                                            "certificatesTableHeader.requester",
                                        )}
                                        size="small"
                                        onChange={handleFilterChange}
                                        value={requester}
                                        onBlur={() => checkFilterUpdateData()}
                                        onKeyDown={checkKeyEnter}
                                    />
                                    <TableSortLabel
                                        active={
                                            orderBy ===
                                            "application.applicant.firstName"
                                        }
                                        direction={
                                            orderBy ===
                                            "application.applicant.firstName"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "application.applicant.firstName",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            renderCell: (params: any) => (
                                <Box>
                                    {params.row.firstNameRequester}{" "}
                                    {params.row.lastNameRequester}
                                </Box>
                            ),
                        },
                        {
                            field: "dateFrom",
                            headerName: t("certificatesTableHeader.startDate"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <DatePicker
                                        label={t(
                                            "certificatesTableHeader.startDate",
                                        )}
                                        value={dateFrom}
                                        onChange={(
                                            newValue,
                                            keyboardInputValue,
                                        ) => {
                                            handleDateFilterChange(
                                                newValue,
                                                keyboardInputValue,
                                                "dateFrom",
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                {...tableTextFieldProps}
                                            />
                                        )}
                                        inputFormat="DD/MM/YYYY"
                                    />

                                    <TableSortLabel
                                        active={
                                            orderBy === "application.dateFrom"
                                        }
                                        direction={
                                            orderBy === "application.dateFrom"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "application.dateFrom",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            valueFormatter: (params: any) =>
                                dayjs(params.value).format("DD/MM/YYYY"),
                        },
                        {
                            field: "dateTo",
                            headerName: t("certificatesTableHeader.endDate"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <DatePicker
                                        label={t(
                                            "certificatesTableHeader.endDate",
                                        )}
                                        value={dateTo}
                                        onChange={(
                                            newValue,
                                            keyboardInputValue,
                                        ) => {
                                            handleDateFilterChange(
                                                newValue,
                                                keyboardInputValue,
                                                "dateTo",
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                {...tableTextFieldProps}
                                            />
                                        )}
                                        inputFormat="DD/MM/YYYY"
                                    />

                                    <TableSortLabel
                                        active={
                                            orderBy === "application.dateTo"
                                        }
                                        direction={
                                            orderBy === "application.dateTo"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "application.dateTo",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            valueFormatter: (params: any) =>
                                dayjs(params.value).format("DD/MM/YYYY"),
                        },
                        {
                            field: "renewDate",
                            headerName: t("certificatesTableHeader.renewDate"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <DatePicker
                                        label={t(
                                            "certificatesTableHeader.renewDate",
                                        )}
                                        value={renewCreationDate}
                                        onChange={(
                                            newValue,
                                            keyboardInputValue,
                                        ) => {
                                            handleDateFilterChange(
                                                newValue,
                                                keyboardInputValue,
                                                "renew",
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                {...tableTextFieldProps}
                                            />
                                        )}
                                        inputFormat="DD/MM/YYYY"
                                    />

                                    <TableSortLabel
                                        active={
                                            orderBy === "application.renewDate"
                                        }
                                        direction={
                                            orderBy === "application.renewDate"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "application.renewDate",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            renderCell: (params: any) => (
                                <Box>
                                    {params.row.renew
                                        ? dayjs(
                                              params.row.renew.creationTime,
                                          ).format("DD/MM/YYYY")
                                        : ""}
                                </Box>
                            ),
                        },
                        {
                            field: "renewReference",
                            headerName: t("certificatesTableHeader.newRequest"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        id="renewReferenceCode"
                                        label={t(
                                            "certificatesTableHeader.newRequest",
                                        )}
                                        size="small"
                                        onChange={handleFilterChange}
                                        value={renewReference}
                                        onBlur={() => checkFilterUpdateData()}
                                        onKeyDown={checkKeyEnter}
                                    />
                                    <TableSortLabel
                                        active={
                                            orderBy === "renew.referenceCode"
                                        }
                                        direction={
                                            orderBy === "renew.referenceCode"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "renew.referenceCode",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            renderCell: (params: any) => (
                                <Box>
                                    {params.row.renew
                                        ? params.row.renew.referenceCode
                                        : ""}
                                </Box>
                            ),
                        },
                        {
                            field: "department",
                            headerName: t("requestsTableHeader.department"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="department-label">
                                            {t(
                                                "requestsTableHeader.department",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="department-label"
                                            multiple
                                            value={selectedDepartments}
                                            onChange={handleDepartmentChange}
                                            renderValue={(selected) =>
                                                selected.join(", ")
                                            }
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "requestsTableHeader.department",
                                                    )}
                                                />
                                            }
                                            MenuProps={tableFilterProps}
                                            onClose={() => {
                                                setCurrentPage(0);
                                                checkFilterUpdateData();
                                            }}
                                        >
                                            {departments.map((department) => (
                                                <MenuItem
                                                    key={department}
                                                    value={department}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            selectedDepartments.indexOf(
                                                                department,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={department}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TableSortLabel
                                        active={orderBy === "user.department"}
                                        direction={
                                            orderBy === "user.department"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "user.department",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                        },
                        {
                            field: "organization",
                            headerName: t("requestsTableHeader.org"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        id="organisation"
                                        label={t("requestsTableHeader.org")}
                                        value={organisation}
                                        onChange={handleOrgChange}
                                        onBlur={() => {
                                            setCurrentPage(0);
                                            checkFilterUpdateData();
                                        }}
                                        onKeyDown={checkKeyEnter}
                                        size="small"
                                    />
                                </Box>
                            ),
                        },
                        {
                            field: "status",
                            headerName: t("certificatesTableHeader.status"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="status-label">
                                            {t(
                                                "certificatesTableHeader.status",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="status-label"
                                            id="status"
                                            multiple
                                            value={status}
                                            onChange={(
                                                event: SelectChangeEvent<
                                                    string[]
                                                >,
                                            ) => {
                                                handleChange(event, "status");
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "certificatesTableHeader.status",
                                                    )}
                                                />
                                            }
                                            renderValue={(selected) =>
                                                selected.join(", ")
                                            }
                                            MenuProps={tableFilterProps}
                                            onClose={() => {
                                                setCurrentPage(0);
                                                checkFilterUpdateData();
                                            }}
                                            onBlur={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {statuses.map((name) => (
                                                <MenuItem
                                                    key={name}
                                                    value={name}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            status.indexOf(
                                                                name,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={name}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TableSortLabel
                                        active={orderBy === "status"}
                                        direction={
                                            orderBy === "status"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("status")
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            renderCell: (params: any) => (
                                <Chip
                                    sx={{
                                        marginLeft: "10px",
                                        backgroundColor: statusStyleCertificate(
                                            getStatus(params.row),
                                        ),
                                        color: "white",
                                    }}
                                    label={getStatus(params.row)}
                                    size="small"
                                />
                            ),
                        },
                    ]}
                    loading={isLoading}
                    rowCount={count}
                    updatePagination={updatePagination}
                    paginationModel={{
                        pageSize: rowsPerPage,
                        page: currentPage,
                    }}
                    onRowClick={(params) => handleClickCertificate(params.row)}
                    onCellClick={handleCellClick}
                    onColumnVisibilityModelChange={handleHeaderChange}
                    columnVisibilityModel={headers}
                    toolbar={[
                        <Button
                            disabled={
                                selected.filter((_) => _.checked).length === 0
                            }
                            sx={{ width: "125px" }}
                            variant="contained"
                            onClick={handleRenewOpen}
                        >
                            {t("renew.completeSubmit")}
                        </Button>,
                    ]}
                    checkboxSelection
                    onRowSelectionModelChange={handleSelectionChange}
                    initialColumnVisibility={initialColumnVisibility}
                    reset={handleReset}
                />
            </Box>

            <RenewBulkModal
                open={openBulkRenew}
                renews={selected
                    .filter((item) => item.checked)
                    .map((item) => {
                        const cert = certificates.find(
                            (cert) => cert.id === item.id,
                        );
                        return {
                            id: item.id,
                            reference: item.reference,
                            name: `${cert?.firstNameRequester} ${cert?.lastNameRequester}`,
                        };
                    })}
                close={setOpenBulkRenew}
                refresh={getAllInfo}
            />
        </Card>
    );
};

export default ListRenews;
